import { computed } from 'vue';
import { useStore } from 'vuex';
import useForeignApiRequest from '@composables/useForeignApiRequest';
import { today, subtractTmDateHours, getDateRangeLengthInHours } from '@utils/tm-date';
import { COMPARISON_MAP_MODES } from '@keys/index';
import { isDateRange } from '@utils/tm-date';

export type TrafficContext = Record<
  EdgeId,
  {
    volume: number;
    flowSum?: number;
    hourSum?: number;
    timestamp?: TmDate;
    geh?: number;
    diff?: number;
    capacity?: number;
    [key: string]: unknown;
  }
>;

type EdgeId = string; // stringified number in case of model id, "extra-" substring in case of extra id e.g. "extra-812".
type LiveTrafficResponse = { liveTraffic: Record<string, { totalVehicleCount: number; timestamp: string }> };

export type Tr = {
  totalVehicleCount?: number;
  fromEdge?: TmFeatureId;
  toEdge?: TmFeatureId;
  nodeId: number;
};

export type HistoricalTraffic = Record<
  EdgeId,
  {
    avgSpeed: number | null;
    avgPersonalVehicleSpeed: number | null;
    avgLightTruckSpeed: number | null;
    avgHeavyTruckSpeed: number | null;
    avgBusSpeed: number | null;
    totalVehicleCount: number;
    personalVehicleCount: number;
    lightTruckCount: number;
    heavyTruckCount: number;
    busCount: number;
    timestamp: string;
  }[]
>;

type HistoricalTrafficResponse = {
  historicalTraffic: HistoricalTraffic;
};

type ModelTrafficResponse = {
  traffic?: Record<
    EdgeId,
    Partial<{
      aadt: number; // annual average daily traffic is an official engineering term
      intervalSum: number;
      totalVehicleCount: number;
      fossilFuel: number;
      electricDrive: number;
      personalVehicleCount: number;
      truckCount: number;
      modelTraffic: number;
    }>
  > | null;
};

type ModelTrResponse = {
  tr?: Tr[];
};

const LATEST_TRAFFIC_REQUEST_LIMIT = 5; // in case the historical traffic for the current hour is not available in the sensors API, we can try to obtain 'current hour - 1' then 'current hour - 2' etc until the traffic is found or the limit specified by this constant is exceeded
const fetchPromises = new Map<string, Promise<any>>(); // re-usable fetch request promises

export default function useTrafficData() {
  const store = useStore();
  const { makeRequest } = useForeignApiRequest();

  const getLiveTrafficData = (): Promise<TrafficContext | null> =>
    _fetchWithReusablePromise({
      url: `${import.meta.env.VITE_API_HOST}dealer/traffic/data/live`,
      responseParser: (result: LiveTrafficResponse) => {
        return Object.keys(result.liveTraffic).reduce<TrafficContext>((acc, id) => {
          const { totalVehicleCount, ...restProps } = result.liveTraffic[id];
          acc[id] = {
            volume: totalVehicleCount,
            ...restProps,
          };
          return acc;
        }, {});
      },
    });

  const getModelTrafficData = ({
    matrixId,
    scenarioId,
    date,
    dateFilters,
  }: {
    matrixId?: number;
    scenarioId?: TmSelectedScenario;
    date?: TmCalendarDate;
    dateFilters?: TmDateFilters;
  } = {}): Promise<TrafficContext | null> => {
    if (matrixId && scenarioId) throw new Error(`Both matrix ${matrixId} and scenario ${scenarioId} provided`);
    const trafficModel = <TmTrafficModel>store.state.scenarios.model;
    const targetScenarioId = scenarioId === 'base' ? undefined : scenarioId; // base scenario (meaning no scenario resp. base model) is the default on the server -> unify requests
    const targetMatrixId = trafficModel?.activeMatrixId === matrixId ? undefined : matrixId; // active matrix is the default on the server -> unify requests
    const targetModelIdentifier = trafficModel?.id || trafficModel?.name || 'default'; // sometimes name is available before the ID (e.g. when the model name is obtained from url)
    const includeUnsavedScenarioChanges = scenarioId && !!store.getters['scenarios/isUsingChangedScenario'](scenarioId); // in case we need to get traffic with unsaved (but already computed) scenario changes

    return _fetchWithReusablePromise({
      url: `${import.meta.env.VITE_API_HOST}models/${targetModelIdentifier}/traffic`,
      params: {
        ..._getDateParams(date),
        ..._getDateFiltersParams(dateFilters),
        ...(targetScenarioId && { scenarioId: targetScenarioId }),
        ...(targetMatrixId && { matrixId: targetMatrixId }),
        ...(includeUnsavedScenarioChanges && { useChanged: true }),
      },
      responseParser: (result: ModelTrafficResponse) => {
        return Object.entries(result.traffic ?? {}).reduce<TrafficContext>((acc, [edgeId, props]) => {
          const { totalVehicleCount, intervalSum, modelTraffic } = props;
          acc[edgeId] = {
            ...props,
            ...(modelTraffic && { baseVolume: modelTraffic }),
            ...(intervalSum && { flowSum: intervalSum }),
            volume: totalVehicleCount || 0,
          };
          return acc;
        }, {});
      },
    });
  };

  const getStaModelTurnRestrictions = async ({
    date,
    scenarioId,
    matrixId,
  }: {
    date: TmCalendarDate;
    scenarioId?: number;
    matrixId?: number;
  }): Promise<Tr[] | null> => {
    if (!date) throw new Error('Returning tr hour data requires valid date');
    if (matrixId && scenarioId) throw new Error(`Both matrix ${matrixId} and scenario ${scenarioId} provided`);
    const trafficModel = <TmTrafficModel>store.state.scenarios.model;
    if (!trafficModel?.id) throw new Error('Traffic model id not yet loaded');

    return _fetchWithReusablePromise({
      url: `${import.meta.env.VITE_API_HOST}models/${trafficModel.id}/node-transit`,
      params: { ..._getDateParams(date), scenarioId, matrixId },
      responseParser: (result: ModelTrResponse): Tr[] | null => {
        if (!result.tr?.length) return null;
        return result.tr.map(({ totalVehicleCount = 0, ...rest }) => ({
          ...rest,
          totalVehicleCount,
        }));
      },
    });
  };

  const getHistoricalTrafficData = (dateFrom: TmDate, dateTo?: TmDate): Promise<TrafficContext | null> =>
    _fetchWithReusablePromise({
      url: `${import.meta.env.VITE_API_HOST}dealer/traffic/data/historical`,
      params: { from: dateFrom, to: dateTo ?? dateFrom, shouldAgg: 'true' },
      responseParser: (result: HistoricalTrafficResponse) => {
        if (!Object.keys(result.historicalTraffic).length) return null;
        const hourSum = !dateTo ? 1 : getDateRangeLengthInHours(dateFrom, dateTo);
        return Object.entries(result.historicalTraffic).reduce((context: TrafficContext, [id, [ogProps]]) => {
          if (!id) return context;
          const flowSum = ogProps.totalVehicleCount;
          context[id] = { ...ogProps, volume: Math.floor(flowSum / hourSum), flowSum, hourSum };
          return context;
        }, {});
      },
    });

  const getHourlyHistoricalTrafficData = async (dateFrom: TmDate, dateTo: TmDate): Promise<HistoricalTraffic | null> =>
    _fetchWithReusablePromise({
      url: `${import.meta.env.VITE_API_HOST}dealer/traffic/data/historical`,
      params: { from: dateFrom, to: dateTo, shouldAgg: 'false' },
      responseParser: (result: HistoricalTrafficResponse): HistoricalTraffic => {
        return result.historicalTraffic;
      },
    });

  /**
   * This Fn tries to find the latest available traffic date from the sensors API
   * Currently the only way to do it is to try to obtain the traffic data on the actual date
   * and if it is not found, try the date minus one hour (max LATEST_TRAFFIC_REQUEST_LIMIT tries)
   * As a side effect we also cache all the traffic data obtained in the process
   * Usually the traffic is available 1-3 hours after it has been measured by the sensors
   * In some edge cases - user might change the calendar date or switch map mode before the latest traffic was found
   * in those cases the function returns null (to prevent any consequential logic)
   * @param {boolean} updateStoredDate if true, also updates the stored calendar date to the latest traffic date (if found)
   */
  const getLatestAvailableHistoricalTrafficDate = async (updateStoredDate: boolean = true): Promise<TmDate> => {
    const initialMapMode = store.state.map.mapMode;
    const initialCalendarDate = store.getters['map/getCalendarDate']({ mapMode: initialMapMode, startDateOnly: true });
    if (initialCalendarDate !== today({ stripMins: true })) return null; // do not try to obtain latest traffic if calendar date is already not the latest
    const latestTrafficDate = await _findLatestHistoricalTrafficDate();
    if (!latestTrafficDate) return null;
    const currentMapMode = store.state.map.mapMode;
    const currentCalendarDate = store.getters['map/getCalendarDate']({ mapMode: initialMapMode, startDateOnly: true });
    if (currentCalendarDate !== initialCalendarDate || currentMapMode !== initialMapMode) return null;
    if (!updateStoredDate) return latestTrafficDate;
    const isComparisonMode = COMPARISON_MAP_MODES.includes(initialMapMode);
    const updatedCalendarDate = isComparisonMode ? [latestTrafficDate, latestTrafficDate] : latestTrafficDate;
    await store.dispatch('map/setCalendarDate', { mapMode: initialMapMode, tmDate: updatedCalendarDate }); // update stored calendar date to the latest traffic date
    return latestTrafficDate;
  };

  const _findLatestHistoricalTrafficDate = async () => {
    let requestsCounter: number = 0;
    let latestTrafficDate: string = today({ stripHours: false, stripMins: true });
    let trafficFound: boolean = false;
    while (!trafficFound && requestsCounter < LATEST_TRAFFIC_REQUEST_LIMIT) {
      console.info('Auto fetching latest available historical traffic on date', latestTrafficDate);
      trafficFound = !!(await getHistoricalTrafficData(latestTrafficDate));
      requestsCounter++;
      if (!trafficFound) latestTrafficDate = <string>subtractTmDateHours(latestTrafficDate, 1);
    }
    return trafficFound ? latestTrafficDate : null;
  };

  const _fetchWithReusablePromise = async <T, R>({
    url,
    params,
    responseParser,
  }: {
    url: Parameters<typeof makeRequest>[0]['url'];
    params?: Parameters<typeof makeRequest>[0]['params'];
    responseParser: (result: T) => R;
  }): Promise<R | null> => {
    const promiseKey = _getPromiseKey(url, params);
    if (fetchPromises.has(promiseKey)) return fetchPromises.get(promiseKey);

    const fetchPromise = new Promise<R | null>((resolve) => {
      makeRequest({
        url,
        method: 'get',
        params,
        message: { error: `Traffic data not available at \n ${url}` },
        timeout: 1000 * 60, // 1 minute,
        onSuccess: (result: T) => resolve(responseParser(result)),
        onFailure: () => resolve(null),
      });
    }).finally(() => fetchPromises.delete(promiseKey)); // delete resolved promise

    fetchPromises.set(promiseKey, fetchPromise);
    return fetchPromise;
  };

  return {
    getLiveTrafficData,
    getModelTrafficData,
    getHistoricalTrafficData,
    getLatestAvailableHistoricalTrafficDate,
    getStaModelTurnRestrictions,
    isLoading: computed(() => fetchPromises.size > 0),
    invalidateTrafficCache,
    getHourlyHistoricalTrafficData,
  };
}

export const invalidateTrafficCache = (args?: any) => {
  // do nothing since we are not caching traffic atm (issue 86c7dxy4k)
  // but keep the function and all its calls for for now
  console.info('traffic invalidation called, but has no effect atm', args);
};

const _getPromiseKey = (
  url: string,
  params: {
    date?: TmCalendarDate;
    dateFilters?: TmDateFilters;
    [key: string]: unknown;
  },
) => {
  const { date, dateFilters, ...simpleParams } = params || {};
  const dateString = date ? (Array.isArray(date) ? `${date[0]}-${date[1]}` : date) : 'no_date';
  const dateFiltersString = dateFilters
    ? Object.keys(dateFilters)
        .sort() // guarantee key order
        .map((key) => dateFilters[key as keyof TmDateFilters])
        .join('_')
    : 'no_date_filters';
  const simpleParamsString = Object.keys(simpleParams)
    .sort() // guarantee key order
    .map((key) => String(simpleParams[key]))
    .join('_');
  return `${url}_${simpleParamsString}_${dateString}_${dateFiltersString}`;
};

const _getDateParams = (date?: TmCalendarDate) => {
  if (!date) return {};
  if (isDateRange(date)) return { dateFrom: date[0], dateTo: date[1] };
  return { dateFrom: date, dateTo: date };
};

const _getDateFiltersParams = (dateFilters?: TmDateFilters) => {
  if (!dateFilters) return {};
  return {
    ...(dateFilters.hours?.length && { hours: dateFilters.hours.join() }),
    ...(dateFilters.days?.length && { days: dateFilters.days.join() }),
    ...(dateFilters.months?.length && { months: dateFilters.months.join() }),
  };
};
